import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import SiteFooter from "../components/SiteFooter";

export default function Result() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const stateFromUrl = searchParams.get("state"); // 'success' | 'failed' | null

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/api/registrations/${id}`);
        setData(data);
        setErr("");
      } catch (e) {
        console.error(e);
        setErr(e?.response?.data?.message || "Failed to load details");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const formatDateTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const statusBadge = () => {
    if (!data) return null;
    const isPaid =
      data.payment_status === "SUCCESS" ||
      data.registration_status === "COMPLETED";

    const bg = isPaid ? "#d1fae5" : "#fee2e2";
    const br = isPaid ? "#10b981" : "#ef4444";
    const text = isPaid ? "#065f46" : "#7f1d1d";
    const label = isPaid ? "Payment Successful" : "Payment Pending";

    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 10px",
          borderRadius: 999,
          border: `1px solid ${br}`,
          background: bg,
          color: text,
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {label}
      </span>
    );
  };

  const amountText =
    data && data.final_amount != null
      ? data.final_amount.toFixed(2)
      : data && data.course_fee != null
      ? data.course_fee.toFixed(2)
      : "-";

  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          background: "#f1f5f9",
          padding: "16px",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 640,
            marginTop: 24,
            background: "#ffffff",
            borderRadius: 16,
            boxShadow: "0 20px 40px rgba(15,23,42,0.12)",
            border: "1px solid #e2e8f0",
            padding: 24,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 22 }}>Registration Receipt</h2>
            {statusBadge()}
          </div>

          {loading && <div>Loading…</div>}
          {err && !loading && (
            <div style={{ color: "#b91c1c", marginBottom: 12 }}>{err}</div>
          )}

          {!loading && data && (
            <>
              {/* Top section: Receipt details */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                  fontSize: 14,
                  marginBottom: 16,
                }}
              >
                <div>
                  <div style={{ color: "#64748b", fontSize: 12 }}>
                    Receipt No
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {data.receipt_no || `REG-${data.id}`}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#64748b", fontSize: 12 }}>
                    Registered Date
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {formatDateTime(data.created_at)}
                  </div>
                </div>
              </div>

              <hr style={{ borderColor: "#e5e7eb", margin: "8px 0 16px" }} />

              {/* Participant info */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                  fontSize: 14,
                }}
              >
                <div>
                  <div style={{ color: "#64748b", fontSize: 12 }}>Name</div>
                  <div style={{ fontWeight: 600 }}>{data.full_name}</div>

                  <div
                    style={{
                      color: "#64748b",
                      fontSize: 12,
                      marginTop: 8,
                    }}
                  >
                    Mobile
                  </div>
                  <div>{data.mobile}</div>

                  <div
                    style={{
                      color: "#64748b",
                      fontSize: 12,
                      marginTop: 8,
                    }}
                  >
                    Gender
                  </div>
                  <div>{data.gender === "M" ? "Male" : data.gender === "F" ? "Female" : "-"}</div>
                </div>

                <div>
                  <div style={{ color: "#64748b", fontSize: 12 }}>Course</div>
                  <div style={{ fontWeight: 600 }}>
                    {data.course_name || "-"}
                  </div>

                  <div
                    style={{
                      color: "#64748b",
                      fontSize: 12,
                      marginTop: 8,
                    }}
                  >
                    Amount Paid (₹)
                  </div>
                  <div style={{ fontWeight: 600 }}>₹ {amountText}</div>

                  <div
                    style={{
                      color: "#64748b",
                      fontSize: 12,
                      marginTop: 8,
                    }}
                  >
                    Reffed by
                  </div>
                  <div>{data.referrer_name || "-"}</div>
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ color: "#64748b", fontSize: 12 }}>Address</div>
                <div style={{ fontSize: 14 }}>
                  {data.address || "-"}
                </div>
              </div>

              {/* Payment info */}
              <div
                style={{
                  marginTop: 16,
                  fontSize: 13,
                  background: "#f8fafc",
                  borderRadius: 12,
                  padding: 12,
                  border: "1px dashed #cbd5e1",
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  Payment Details
                </div>
                <div>
                  <span style={{ color: "#64748b" }}>Order Id: </span>
                  <span>{data.razorpay_order_id || "-"}</span>
                </div>
                <div>
                  <span style={{ color: "#64748b" }}>Payment Id: </span>
                  <span>{data.razorpay_payment_id || "-"}</span>
                </div>
              </div>

              {/* Footer */}
              <div
                style={{
                  marginTop: 20,
                  fontSize: 12,
                  color: "#64748b",
                  textAlign: "center",
                }}
              >
                Thank you for registering with YogaPay.
              </div>
            </>
          )}

          <div
            style={{
              marginTop: 20,
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <Link
              to="/"
              style={{
                padding: "10px 16px",
                borderRadius: 999,
                border: "1px solid #cbd5e1",
                textDecoration: "none",
                fontSize: 14,
                color: "#0f172a",
              }}
            >
              ← Back to Home
            </Link>
            <button
              type="button"
              onClick={() => window.print()}
              style={{
                padding: "10px 16px",
                borderRadius: 999,
                border: "none",
                background: "#0f766e",
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Print Receipt
            </button>
          </div>
        </div>
      </div>

      <SiteFooter />
    </>
  );
}
